import { useMe } from '@hooks/useMe'
import PhotoGallery from '@components/FileViewer/PhotoGallery'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ChatMessage from './ChatMessage'
import {
	ChatAboutButton,
	DiscussionDay,
	DiscussionHeading,
	DiscussionPlaceholder,
	DiscussionSection,
	ShowEarlierButton,
} from './ChatDiscussion.styles'
import { cardRoute, cardRoutePath, chatPath, sameTag, type ChatCard, type ChatPhoto } from './core'
import { useDiscussion } from './useDiscussion'

/** What the Chat page reads from its location when "Chat about this" sends it there. */
export interface ChatLocationState {
	/** The resource to add to the Message being written. */
	chatAbout?: ChatCard
}

interface ChatDiscussionProps {
	tripId: string
	/** The card "Chat about this" adds, which names the Discussion. */
	resource: ChatCard
	/** Called before leaving for the Chat or another resource, as a modal closes itself. */
	onLeave?: () => void
	className?: string
}

/**
 * A resource's Discussion on its detail surface: a "Discussion" heading, the Messages
 * about it as the Chat draws them, and "Chat about this" at the end, which goes to the
 * Chat with the resource added to the Message being written. Clicking a Message opens the
 * Chat on it; reactions and Add as idea work here too. Ported from iOS
 * `ChatDiscussionView` and `ChatDiscussionController`.
 */
const ChatDiscussion: React.FC<ChatDiscussionProps> = ({ tripId, resource, onLeave, className }) => {
	const navigate = useNavigate()
	const { data: me } = useMe()
	const { items, hasOlder, placeholder, loadOlder, actions } = useDiscussion(tripId, resource, me?.id)
	const [gallery, setGallery] = useState<{ photos: ChatPhoto[]; index: number } | null>(null)

	const go = (path: string, state?: ChatLocationState) => {
		onLeave?.()
		navigate(path, state ? { state } : undefined)
	}

	/** The resource's own card is already on screen; any other opens as in the Chat. */
	const openCard = (card: ChatCard) => {
		const route = cardRoute(card)
		if (!route || sameTag(card, resource)) return
		go(cardRoutePath(tripId, route))
	}

	return (
		<DiscussionSection aria-label="Discussion" className={className}>
			<DiscussionHeading>Discussion</DiscussionHeading>
			{hasOlder && (
				<ShowEarlierButton type="button" onClick={loadOlder}>
					Show earlier messages
				</ShowEarlierButton>
			)}
			{items.length > 0 && (
				<div>
					{items.map(item => {
						if (item.type === 'day') return <DiscussionDay key={item.id}>{item.title}</DiscussionDay>
						// A Discussion keeps no read position.
						if (item.type === 'unreadDivider') return null
						return (
							<ChatMessage
								key={item.id}
								row={item.row}
								onOpenCard={openCard}
								onOpenQuote={messageId => go(chatPath(tripId, messageId))}
								onOpenMessage={messageId => go(chatPath(tripId, messageId))}
								onReact={actions.toggleReaction}
								onAddLinkAsIdea={actions.addLinkAsIdea}
								onOpenPhotos={(photos, index) => setGallery({ photos, index })}
							/>
						)
					})}
				</div>
			)}
			{placeholder && <DiscussionPlaceholder>{placeholder}</DiscussionPlaceholder>}
			<ChatAboutButton type="button" onClick={() => go(chatPath(tripId), { chatAbout: resource })}>
				Chat about this
			</ChatAboutButton>
			<PhotoGallery photos={gallery?.photos ?? []} startIndex={gallery?.index ?? null} onClose={() => setGallery(null)} />
		</DiscussionSection>
	)
}

export default ChatDiscussion
